import { useCallback, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'

import { formatOccupantsShort } from 'lib/offer/occupancyUtils'

import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import Pane from 'components/Common/Pane'
import Group from 'components/utils/Group'
import type { RoomRateViews } from 'components/OfferPage/Property/singlePropertyUtils'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'

import PropertyIndividualRoomSelectRoom from './PropertyIndividualRoomSelectRoom'

interface Props {
  occupancyRoom: OfferPageRoom
  /** Room-scoped options (capacity / guest limits already applied). */
  selectableRoomOptions: Array<RoomRateViews>
  currentPropertyRoomTypeId: string
  rooms: Array<OfferPageRoom>
  duration: number
  checkInDate?: string
}

function PropertyIndividualRoomTypeSelectionModal(props: Props) {
  const {
    occupancyRoom,
    rooms,
    selectableRoomOptions,
    currentPropertyRoomTypeId,
    duration,
    checkInDate,
  } = props

  const intl = useIntl()
  const modalElement = useModalElementContext()
  const dispatch = useOfferPageDispatch()

  // Read age thresholds from the current room option instead of the offer. They are per-room but
  // uniform across a property in practice; fall back to any option if the current one is absent.
  const currentRoomOption = selectableRoomOptions.find(option => option.propertyRoom.id === currentPropertyRoomTypeId)
  const occupancyFormatOptions = (currentRoomOption ?? selectableRoomOptions[0])?.propertyRoom.ageCategoryThresholds

  const roomIndex = rooms.findIndex(r => r.id === occupancyRoom.id)
  const roomNumber = roomIndex >= 0 ? roomIndex + 1 : 1

  useEffect(() => {
    if (roomIndex === -1) {
      modalElement.resolve()
    }
  }, [modalElement, roomIndex])

  const close = useCallback(() => {
    modalElement.resolve()
  }, [modalElement])

  const snackbarActionLabel = intl.formatMessage({
    id: '9zynkmMImt4w2_jv9nfx',
    defaultMessage: 'OK',
  })

  const noDatesSelected = !checkInDate

  // Ordering is done by getPropertyIndividualRowsByRoomId; this only drops options that have no
  // rate for the chosen dates, and blanks rates before dates are picked.
  const roomOptionsForSelection = useMemo(() => selectableRoomOptions
    .filter(roomOption => noDatesSelected || roomOption.rates.length > 0)
    .map(roomOption => ({ ...roomOption, rates: noDatesSelected ? [] : roomOption.rates })),
  [noDatesSelected, selectableRoomOptions])

  const onSelectOption = useCallback((roomTypeId: string, optionId: string | undefined) => {
    if (!roomTypeId || (!noDatesSelected && !optionId)) {
      return
    }
    // With no dates there are no rates to pick, so a room-type switch must be rate-less; the
    // incoming optionId is the prior room's rate key (passed through as currentOptionId) and would
    // otherwise be carried onto the new room type, which matches no rate for it.
    const resolvedOptionId = noDatesSelected ? undefined : optionId
    const nextRooms = rooms.map(room => room.id === occupancyRoom.id ?
        // Bundle selections belong to the rate they were ticked on — a manual room/rate
        // change drops them, matching the autoselection invariant, rather than silently
        // carrying a charge onto a rate the shopper never re-picked them for.
        { ...room, roomId: roomTypeId, rateId: resolvedOptionId, optionalBundleIds: undefined } :
      room,
    )
    dispatch({ type: OfferPageActions.setRooms, rooms: nextRooms })
    showSnackbar(
      intl.formatMessage(
        {
          id: 'bwphPz4LS4kHtcbLd11s',
          defaultMessage: 'Room {roomNumber} has been updated',
        },
        { roomNumber },
      ),
      'success',
      { action: { label: snackbarActionLabel } },
    )
    close()
  }, [close, dispatch, intl, noDatesSelected, occupancyRoom, roomNumber, rooms, snackbarActionLabel])

  const isDesktopLayout = useScreenSizeUp('tablet')
  const optionVariant = isDesktopLayout ? 'stacked' : 'carousel'

  if (roomIndex === -1) {
    return null
  }

  return (
    <ModalBase height="full" size="L">
      <ModalHeader
        title={intl.formatMessage(
          {
            id: 'mw9BZYlZITUmySXN-Z2h',
            defaultMessage: 'Room {roomNumber} of {totalRooms}',
          },
          {
            roomNumber,
            totalRooms: rooms.length,
          },
        )}
        subtitle={formatOccupantsShort(occupancyRoom, occupancyFormatOptions)}
        onCloseButtonClick={close}
      />
      <ModalBody>
        <Pane background="neutral-six" tabletBackground="none" padding="8 0 0 0" tabletPadding={12} desktopPadding={20}>
          <Group direction="vertical" gap={8} tabletGap={12}>
            {roomOptionsForSelection.length === 0 && (
              <MessageBanner kind="warning" density="compact">
                {intl.formatMessage({
                  id: 'eZABPHfBhIi3EDkk3p3e',
                  defaultMessage: 'No room options available right now.',
                })}
              </MessageBanner>
            )}
            {roomOptionsForSelection.map((roomOption) => <PropertyIndividualRoomSelectRoom
              key={roomOption.roomId}
              optionVariant={optionVariant}
              roomOption={roomOption}
              currentRoomTypeId={currentPropertyRoomTypeId}
              currentOptionId={occupancyRoom.rateId}
              occupancyRoom={occupancyRoom}
              duration={duration}
              noRateSelectionEnabled={noDatesSelected}
              onSelectRate={onSelectOption}
            />)}
          </Group>
        </Pane>
      </ModalBody>
    </ModalBase>
  )
}

export default withOfferPageAndReduxState((state) => {
  return {
    rooms: state.rooms,
    duration: state.duration,
    checkInDate: state.checkInDate,
  }
}, PropertyIndividualRoomTypeSelectionModal)
